import { UserId } from "@sp-crm/core";
import { Radio, RadioOption } from "components/shared/radio";
import { SignaturePreview } from "components/shared/signature-preview";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { WorkflowActorMode } from "generated/graphql";
import { produce } from "immer";
import React, { useMemo } from "react";
import { entityTypeLabel, entityTypeNoun } from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface EmailActionEditorProps {
    workflow: EditableWorkflow;
    onChange: (workflow: EditableWorkflow) => void;
}

export const EmailSenderEditor: React.FC<EmailActionEditorProps> = props => {
    const { workflow, onChange } = props;

    const editable = !!workflow.regionVisibility?.editable;
    const noun = entityTypeNoun(workflow.attachTo);
    const label = entityTypeLabel(workflow.attachTo);

    const senderOptions: RadioOption[] = useMemo(
        () => [
            {
                key: WorkflowActorMode.AssignedUser,
                text: `${label}'s assigned user`,
                helpText: `Send the email using the ${noun}'s assigned user`,
            },
            {
                key: WorkflowActorMode.DefaultActor,
                text: "Specific user",
                helpText: "Send the email with a single user of your choice.",
            },
        ],
        [noun, label],
    );

    const handleActorModeChange = (actorMode: WorkflowActorMode) => {
        const newWorkflow = produce(workflow, draft => {
            draft.actorMode = actorMode;
        });

        onChange(newWorkflow);
    };

    const handleActorIdChange = (actorId: SelectableUser) => {
        const newWorkflow = produce(workflow, draft => {
            draft.defaultActorId = actorId as UserId;
        });

        onChange(newWorkflow);
    };

    return (
        <div className="space-y-2">
            <SignaturePreview />
            <Radio
                label=""
                options={senderOptions}
                value={workflow.actorMode}
                onChange={handleActorModeChange}
                disabled={!editable}
            />
            <div
                className={
                    workflow.actorMode === WorkflowActorMode.DefaultActor ? "pl-6" : ""
                }>
                <UserSelect
                    label={
                        workflow.actorMode === WorkflowActorMode.DefaultActor ? (
                            ""
                        ) : (
                            <div>
                                <p>Email sender fallback</p>
                                <p className="text-sm text-gray-500">{`This user will send the email if the ${noun} is unassigned, or if the assigned user's account is deactivated or no longer has access to this office.`}</p>
                            </div>
                        )
                    }
                    value={workflow.defaultActorId}
                    onChange={handleActorIdChange}
                    includeEveryone={false}
                    includeUnassigned={false}
                    disabled={!editable}
                />
            </div>
        </div>
    );
};

export const EmailActionEditor: React.FC<EmailActionEditorProps> = props => (
    <div className="space-y-2">
        <h3 className="font-medium text-gray-900">Email sender</h3>
        <EmailSenderEditor workflow={props.workflow} onChange={props.onChange} />
    </div>
);
